// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
import React, { useCallback, useContext, useState, memo } from 'react';
import { useQuery } from 'urql';
import { Environment } from '@ta/lithium.context';
import useSearchResults, { MIN_QUERY_LENGTH } from './use-search-results/useSearchResults';
import TypeaheadUI, { type TypeaheadColor, type TypeaheadStyle } from './TypeaheadUI';
import NearbyLink from './NearbyLink';
import QuickLinks from './QuickLinks';
import RecentLocations from './RecentLocations';
import type { TypeaheadScope, TypeaheadItem } from './types';
import SearchResultsPlaceholderQuery from './SearchResultsPlaceholderQuery.gql';

const KEY_ENTER = 13;
const KEY_ESCAPE = 27;
const KEY_UP = 38;
const KEY_DOWN = 40;

const elementContains = (parentElement: EventTarget, childElement: EventTarget) =>
  parentElement instanceof Node &&
  childElement instanceof Node &&
  parentElement.contains(childElement);

type Props = {|
  /**
   * The UI placement of the typeahead for tracking purposes (e.g. 'MASTHEAD' or 'Home_geopicker')
   */
  source: string,

  /**
   * The maximum number of results to show
   */
  maxItems?: number,

  /**
   * What color scheme to use
   */
  color?: TypeaheadColor,

  /**
   * The size and style to use. Defaults to box.
   */
  styleVariant?: TypeaheadStyle,

  /**
   * Ghost text to show while nothing is entered in the search box
   */
  placeholderText?: string,

  /**
   * True to hide the typeahead. If it's already open, this has no effect until it's closed.
   */
  hidden?: boolean,

  /**
   * Called when the typeahead overlay is closed
   */
  onClose?: () => mixed,

  /**
   * The current scope (geo or nearby). There's only one field in the typeahead - the context is
   * passed down from the page it appears in.
   */
  scope: TypeaheadScope,
|};

/**
 * The top level typeahead comopnent. This manages the UI state of the search bar.
 *
 * The API interactions are in the {@link ./useSearchResults} hook, and the input element is
 * rendered by {@link ./TypeaheadUI}.
 */
const Typeahead = ({
  source,
  maxItems = 10,
  color,
  styleVariant,
  placeholderText,
  hidden,
  onClose: close = () => {},
  scope,
}: Props) => {
  const { code: locale } = useContext(Environment).locale;

  // The current text entered in the search box
  const [query, setQuery] = useState('');

  // The latest search results from the API
  const {
    results,
    hasResponse,
    logImpression,
    logSelection,
    logSearchAction,
    searchSessionId,
  } = useSearchResults(query, source, scope, maxItems);

  // True if we selected a result and should show a loading animation while the page unloads
  const [isLoading, setIsLoading] = useState(false);

  // True if the overlay should be visible (either with results or the default overview)
  const [isOpen, setIsOpen] = useState(false);

  // Which result is currently selected using keyboard navigation. This result is highlighted in
  // the UI, and pressing Enter navigates to it.
  const [selectedIndex, setSelectedIndex] = useState<?number>(null);

  /**
   * Track that a search result was selected
   */
  const trackSelect = useCallback(
    (result: TypeaheadItem, interactionType: *, isRecentLocation: boolean = false) => {
      logSelection(
        interactionType,
        isRecentLocation,
        result,
        results.length,
        results.indexOf(result),
      );
    },
    [results, logSelection],
  );

  /**
   * Show the results overlay
   */
  const onOpen = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setSelectedIndex(null);
      logImpression();
    }
  }, [isOpen, logImpression]);

  /**
   * Close the results overlay
   */
  const onClose = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      setSelectedIndex(null);
      close();
    }
  }, [isOpen, close]);

  /**
   * Update the query and reset the keyboard navigation state
   */
  const onChange = useCallback(
    (value: string) => {
      setQuery(value);
      setSelectedIndex(null);

      // Pop the results once a query is entered
      if (value.length >= MIN_QUERY_LENGTH) {
        onOpen();
      }
    },
    [onOpen],
  );

  /**
   * Show the results when keyboard focus moves into the typeahead (usually because it was clicked)
   */
  const onFocus = useCallback(
    (evt: SyntheticFocusEvent<HTMLElement>) => {
      const typeaheadElement = evt.currentTarget;
      const blurredElement = evt.relatedTarget;

      if (blurredElement == null || !elementContains(typeaheadElement, blurredElement)) {
        onOpen();
      }
    },
    [onOpen],
  );

  /**
   * Close the results when keyboard focus moves outside of the typeahead
   */
  const onBlur = useCallback(
    (evt: SyntheticFocusEvent<HTMLElement>) => {
      const typeaheadElement = evt.currentTarget;
      const focusedElement = evt.relatedTarget;

      if (focusedElement != null && !elementContains(typeaheadElement, focusedElement)) {
        onClose();
      }
    },
    [onClose],
  );

  /**
   * Handlers for keyboard navigation
   */
  const onKeyDown = useCallback(
    (evt: SyntheticKeyboardEvent<*>) => {
      switch (evt.keyCode) {
        // The up arrow selects the previous result, or opens the results if they're currently
        // closed.
        case KEY_UP:
          if (isOpen) {
            setSelectedIndex(prevSelectedIndex => {
              if (prevSelectedIndex == null) {
                return results.length - 1;
              }

              if (prevSelectedIndex > 0) {
                return prevSelectedIndex - 1;
              }

              return null;
            });
          } else {
            onOpen();
          }

          evt.preventDefault();
          break;

        // The down arrow selects the next result, or opens the results if they're currently closed.
        case KEY_DOWN:
          if (isOpen) {
            setSelectedIndex(prevSelectedIndex => {
              if (prevSelectedIndex == null) {
                return 0;
              }

              if (prevSelectedIndex < results.length - 1) {
                return prevSelectedIndex + 1;
              }

              return null;
            });
          } else {
            onOpen();
          }

          evt.preventDefault();
          break;

        // Enter navigates to the selected result. If no result is selected with keyboard
        // navigation, the default browser behavior will submit the query.
        case KEY_ENTER:
          if (selectedIndex != null && results[selectedIndex] != null) {
            trackSelect(results[selectedIndex], 'enter');
            // eslint-disable-next-line no-restricted-globals
            window.location = results[selectedIndex].url;
            setIsLoading(true);
            evt.preventDefault();
          }
          break;

        // Escape closes the results
        case KEY_ESCAPE:
          onClose();
          evt.preventDefault();
          break;

        default:
      }
    },
    [isOpen, selectedIndex, results, onClose, onOpen, trackSelect],
  );

  /**
   * When a search is submitted, switch the UI to a loading state. This will be displayed until
   * the first render of the results page.
   */
  const onSubmit = useCallback(() => {
    logSearchAction();
    setIsLoading(true);
  }, [logSearchAction]);

  /**
   * Callback for when a search result is hovered or tabbed into. We reset the custom keyboard
   * navigation state, since the user is using a pointing device to navigate and we don't want to
   * show multiple things selected.
   */
  const onResultHovered = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  /**
   * Callback for when a search result is clicked. This tracks the click and shows a loading state
   */
  const onResultClicked = useCallback(
    (result: TypeaheadItem) => {
      trackSelect(result, 'click');
      setIsLoading(true);
    },
    [trackSelect],
  );

  /**
   * Callback for when a recent location is clicked. This tracks the click and shows a loading state
   */
  const onRecentLocationClicked = useCallback(
    (result: TypeaheadItem) => {
      trackSelect(result, 'click', true);
      setIsLoading(true);
    },
    [trackSelect],
  );

  const [{ data: searchResultsPlaceholderData }] = useQuery({
    query: SearchResultsPlaceholderQuery,
    variables: {
      locale,
      geoId: scope.geoId || 0,
      params: scope.geoId != null && scope.geoId > 1 ? { geo: String(scope.geoId) } : {},
    },
  });

  // A placeholder component displayed when the typeahead overlay is open but no query is entered.
  // This includes a nearby link, the standard BU quicklinks, and recently viewed locations
  const resultsPlaceholder = (
    <>
      <NearbyLink />
      <QuickLinks
        geoName={searchResultsPlaceholderData?.locations?.[0]?.name || null}
        links={searchResultsPlaceholderData?.globalNavLinks?.sublinks || []}
        geoId={scope.geoId || 1}
      />
      <RecentLocations
        locations={searchResultsPlaceholderData?.insightProfile?.locations || []}
        onResultClicked={onRecentLocationClicked}
      />
    </>
  );

  if (hidden && !isOpen) {
    return null;
  }

  return (
    <TypeaheadUI
      color={color}
      styleVariant={styleVariant}
      placeholderText={placeholderText}
      query={query}
      geoId={scope.geoId || 0}
      resultsPlaceholder={hasResponse ? null : resultsPlaceholder}
      onChange={onChange}
      onClose={onClose}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onSubmit={onSubmit}
      onResultHovered={onResultHovered}
      onResultClicked={onResultClicked}
      onOpen={onOpen}
      isLoading={isLoading}
      isOpen={isOpen}
      selectedIndex={selectedIndex}
      results={results}
      searchSessionId={searchSessionId}
    />
  );
};

// eslint-disable-next-line ta-code-conventions/require-jsdoc
export default memo<Props>(Typeahead);

export { default as getTypeaheadScopeFromRoute } from './getTypeaheadScopeFromRoute';
